import React, { Fragment, useCallback, useEffect, useMemo, useRef } from "react"
import { Box, Button, Flex, FlexProps, Text, Image } from "@chakra-ui/react"
import { graphql, useStaticQuery } from "gatsby"

import { useCart } from "@app/hooks/useCart"
import { useCartUpsell } from "@app/hooks/useCartUpsell"
import { useAppContext } from "@app/providers/app"
import { useCartContext } from "@app/providers/cart"
import { useConfigContext } from "@app/providers/config"
import { useRoutes } from "@app/hooks/useRoutes"
import { CartItem } from "@app/components/Cart/CartItem"
import { CartUpsell } from "@app/components/Cart/CartUpsell"
import Link from "@app/components/Link"
import { useImageResolver } from "@app/hooks/useImageResolver"
import type { CartLine } from "@shopify/hydrogen-react/storefront-api-types"

type Props = {
  drawer?: boolean
} & FlexProps

export const CartList: React.FC<Props> = React.memo(({ drawer = false, ...props }) => {
  const { settings } = useConfigContext()
  const { anyUrlResolver } = useRoutes()
  const { cart, linesCount } = useCartContext()
  const { state, dispatch } = useAppContext()
  const { emptyCart } = useStaticQuery<GatsbyTypes.StaticCartEmptyQuery>(graphql`
    query StaticCartEmpty {
      emptyCart: sanityPageCart {
        emptyCartHeading
        emptyCartImage: _rawEmptyCartImage(resolveReferences: { maxDepth: 2 })
        emptyCartButton {
          ...LinkFragment
        }
      }
    }
  `)

  const { resolvedImage } = useImageResolver(emptyCart?.emptyCartImage)
  const emptyCartLink = anyUrlResolver(emptyCart?.emptyCartButton)

  const { cartUpsellEnabled } = useCartUpsell()
  const { removeLegacyBundles } = useCart()

  // TODO: Remove this after March 2026 - legacy bundle cleanup no longer needed
  const hasCleanedLegacyBundles = useRef(false)
  useEffect(() => {
    if (cart?.lines?.length && !hasCleanedLegacyBundles.current) {
      hasCleanedLegacyBundles.current = true
      removeLegacyBundles()
    }
  }, [cart?.lines, removeLegacyBundles])

  const onClose = useCallback(() => {
    dispatch({
      type: "cart",
      payload: !state.activeCart,
    })
  }, [dispatch, state.activeCart])

  /**
   * Group cart lines by _gift_bundle_id attribute.
   * Bundle items no longer use lineComponents (Cart Transform merge is disabled for TAF discount support).
   * Instead, we group items by their _gift_bundle_id attribute:
   * - Create a virtual bundle parent using bundle info stored in attributes
   * - ALL bundle items become children in the "What's Inside" section
   * - Non-bundle items are rendered individually
   */
  const { displayItems, bundleChildrenMap, bundleInfoMap } = useMemo(() => {
    const lines = cart?.lines || []
    const bundleIdAttr = settings.cart.bundleIdAttribute

    // Group lines by bundle_id
    const bundleGroups: Record<string, CartLine[]> = {}
    const nonBundleLines: CartLine[] = []

    lines.forEach(line => {
      const bundleId = line.attributes?.find(a => a.key === bundleIdAttr)?.value
      if (bundleId) {
        if (!bundleGroups[bundleId]) {
          bundleGroups[bundleId] = []
        }
        bundleGroups[bundleId].push(line)
      } else {
        nonBundleLines.push(line)
      }
    })

    // Build the display items array, children map, and bundle info map
    const childrenMap: Record<string, CartLine[]> = {}
    const infoMap: Record<string, { title: string; image: string; handle: string; totalPrice: number; tags?: string[] }> = {}
    const bundleParents: CartLine[] = []

    Object.entries(bundleGroups).forEach(([bundleId, bundleLines]) => {
      // Use first item as the "parent" for rendering purposes, but display bundle product info
      const firstItem = bundleLines[0]
      bundleParents.push(firstItem)

      // ALL bundle items are children (shown in "What's Inside")
      childrenMap[bundleId] = bundleLines

      // Extract bundle product info from attributes (stored when bundle was added to cart)
      const bundleTitle = firstItem.attributes?.find(a => a.key === settings.cart.bundleTitleAttribute)?.value || ""
      const bundleImage = firstItem.attributes?.find(a => a.key === settings.cart.bundleImageAttribute)?.value || ""
      const bundleHandle = firstItem.attributes?.find(a => a.key === settings.cart.bundleHandleAttribute)?.value || ""
      const bundleTagsRaw = firstItem.attributes?.find(a => a.key === settings.cart.bundleTagsAttribute)?.value
      const bundleTags: string[] = bundleTagsRaw ? JSON.parse(bundleTagsRaw) : []

      // Calculate total price from all bundle items
      const totalPrice = bundleLines.reduce((sum, line) => sum + Number(line.cost?.totalAmount?.amount || 0), 0)

      infoMap[bundleId] = { title: bundleTitle, image: bundleImage, handle: bundleHandle, totalPrice, tags: bundleTags }
    })

    // Combine and sort: bundles first, then non-bundles, with GWP/gift wrap at end
    const allItems = [...bundleParents, ...nonBundleLines]
    const sortedItems = allItems.sort((left, right) => {
      const leftAttribute = left.attributes.find(
        object => object.key == settings.cart.gwpAttribute || object.key == settings.cart.giftWrapAttribute
      )
      const rightAttribute = right.attributes.find(object => object.key == settings.cart.gwpAttribute)
      if (leftAttribute) {
        return -1
      } else if (rightAttribute) {
        return 1
      } else {
        return 0
      }
    })

    return { displayItems: sortedItems, bundleChildrenMap: childrenMap, bundleInfoMap: infoMap }
  }, [
    cart?.lines,
    settings.cart.bundleIdAttribute,
    settings.cart.bundleTitleAttribute,
    settings.cart.bundleImageAttribute,
    settings.cart.bundleHandleAttribute,
    settings.cart.bundleTagsAttribute,
    settings.cart.gwpAttribute,
    settings.cart.giftWrapAttribute,
  ])

  return (
    <Fragment>
      {linesCount > 0 ? (
        <Box px={0} pb={4} {...props}>
          {displayItems?.map(item => {
            const bundleId = item.attributes?.find(a => a.key === settings.cart.bundleIdAttribute)?.value
            const bundleChildren = bundleId ? bundleChildrenMap[bundleId] : undefined
            const bundleInfo = bundleId ? bundleInfoMap[bundleId] : undefined
            return <CartItem key={item?.id} item={item} bundleChildren={bundleChildren} bundleInfo={bundleInfo} />
          })}
          {cartUpsellEnabled && <CartUpsell />}
        </Box>
      ) : (
        <Flex
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          flexDirection="column"
          p={drawer ? "20px" : "unset"}
          pb={drawer ? "20px" : [6, 24]}
          w={drawer ? "100%" : "unset"}
          flex={drawer ? "1 1 auto" : "unset"}
          minH={drawer ? "fit-content" : "unset"}
        >
          <Flex gap={12} direction="column" maxWidth="366px" alignItems="center">
            <Text size="lg" variant="medium" dangerouslySetInnerHTML={{ __html: emptyCart?.emptyCartHeading || "" }} />
            {resolvedImage && <Image src={resolvedImage?.src} mx="auto" />}
            {emptyCartLink && emptyCartLink?.url && emptyCartLink?.title && (
              <Box>
                <Button as={Link} onClick={onClose} variant="primary" py={3.5} height="auto" w="fit-content" to={emptyCartLink.url}>
                  {emptyCartLink.title}
                </Button>
              </Box>
            )}
          </Flex>
        </Flex>
      )}
    </Fragment>
  )
})
